import React from "react";
import { createRoot } from "react-dom/client";
import { FileText, Printer } from "lucide-react";
import { fetchConsolidationGeotabComparison } from "../../api/geotabApi.js";
import { buildTransportStatement, buildVoyageGpsProof, hasTransportStatement, voyageGpsKey } from "../../utils/transportStatement.js";
import { openDetachedTab } from "../../utils/detachedWindow.js";
import { BillingDocumentBoundary, BillingDocumentDialog, BillingDocumentError } from "./BillingClientExplanationLauncher.jsx";
import "./TransportStatementView.css";

const StatementView = React.lazy(() => import("./TransportStatementView.jsx"));
// Polices du cartable, chargées seulement quand un relevé s'ouvre.
const STATEMENT_FONTS = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@300;400;600;700;800&display=swap";

function useStatementFonts(targetDocument) {
  React.useEffect(() => {
    if (targetDocument.head.querySelector(`link[href="${STATEMENT_FONTS}"]`)) return;
    targetDocument.head.append(Object.assign(targetDocument.createElement("link"), { rel: "stylesheet", href: STATEMENT_FONTS }));
  }, [targetDocument]);
}
const PRINT_BUTTON = "inline-flex min-h-9 shrink-0 items-center gap-2 rounded-[4px] border border-accent bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";

/** Données Geotab de chaque voyage, lues depuis Consolidation par journée. */
function useStatementEvidence(model) {
  const [evidence, setEvidence] = React.useState({ loading: true, proofs: new Map(), note: "" });
  React.useEffect(() => {
    if (!model) return undefined;
    let active = true;
    // Une journée par date et par période : la livraison du Soir a sa propre comparaison.
    const days = [...new Map(model.voyages.filter((voyage) => voyage.date).map((voyage) => [voyageGpsKey(voyage), voyage])).values()];
    const ownOrders = model.voyages.flatMap((voyage) => voyage.orders);
    Promise.allSettled(days.map((voyage) => fetchConsolidationGeotabComparison(voyage.date, voyage.period))).then((results) => {
      if (!active) return;
      const byDay = new Map(days.map((voyage, index) => [voyageGpsKey(voyage), results[index].status === "fulfilled" ? results[index].value : null]));
      const proofs = new Map(model.voyages.map((voyage) => [voyage.key, buildVoyageGpsProof(voyage, byDay.get(voyageGpsKey(voyage)), ownOrders)]));
      const found = [...proofs.values()].filter(Boolean).length;
      const refused = results.some((result) => result.status === "rejected" && result.reason?.status === 403);
      const note = found === model.voyages.length ? "" : found ? `Données Geotab disponibles pour ${found} voyages sur ${model.voyages.length}.`
        : refused ? "Annexe Geotab absente : votre accès ne comprend pas Geotab en Consolidation."
          : "Annexe Geotab absente : aucune donnée Geotab trouvée pour ces voyages.";
      setEvidence({ loading: false, proofs, note });
    });
    return () => { active = false; };
  }, [model]);
  return evidence;
}

function StatementDialog({ invoice, targetWindow, onClose }) {
  const model = React.useMemo(() => {
    try { return buildTransportStatement(invoice); } catch { return null; }
  }, [invoice]);
  const evidence = useStatementEvidence(model);
  const targetDocument = targetWindow.document;
  useStatementFonts(targetDocument);
  async function print() {
    await targetDocument.fonts?.ready;
    // Le titre de la page devient le nom proposé pour le PDF.
    const title = targetDocument.title;
    targetDocument.title = `Relevé de transport - ${model.clientName} - ${model.periodStart} au ${model.periodEnd}`;
    targetWindow.print();
    targetDocument.title = title;
  }
  return <BillingDocumentDialog label="Relevé de transport" backLabel="Retour au brouillon" onClose={onClose} targetDocument={targetDocument}
    note={!model ? "" : evidence.loading ? "Recherche des données Geotab…" : evidence.note || "Format Lettre, prêt à imprimer"}
    actions={<button type="button" className={PRINT_BUTTON} disabled={!model || evidence.loading} onClick={print}>
      <Printer size={16} aria-hidden="true" /> Imprimer ou PDF
    </button>}>
    <BillingDocumentBoundary title="Ce relevé ne peut pas être affiché.">
      {model ? <React.Suspense fallback={<p role="status" className="billing-client-explanation-state">Préparation du relevé…</p>}>
        <StatementView model={model} proofs={evidence.proofs} />
      </React.Suspense> : <BillingDocumentError title="Ce relevé ne peut pas être affiché." />}
    </BillingDocumentBoundary>
  </BillingDocumentDialog>;
}

/** « Relevé de transport » d'un brouillon Consolidation : ce que l'entreprise facturée recevra, avant l'émission. */
export default function TransportStatementLauncher({ invoice, disabled = false, title, className }) {
  const [error, setError] = React.useState("");
  if (!hasTransportStatement(invoice)) return null;
  function open() {
    const tab = openDetachedTab({ title: "Relevé de transport" });
    if (!tab) {
      setError("Autorisez l’ouverture d’un nouvel onglet dans votre navigateur pour voir le relevé.");
      return;
    }
    setError("");
    const root = createRoot(tab.mountNode);
    tab.popupWindow.addEventListener("pagehide", () => root.unmount(), { once: true });
    root.render(<StatementDialog invoice={invoice} targetWindow={tab.popupWindow} onClose={() => tab.popupWindow.close()} />);
  }
  return <>
    <button type="button" className={className} disabled={disabled} title={title} onClick={open}>
      <FileText size={14} aria-hidden="true" />Voir le relevé
    </button>
    {error ? <span role="alert">{error}</span> : null}
  </>;
}
